import React from 'react';
import * as util from '#packages/util';
import { translate } from '#packages/i18n';
import {
  Button,
  DropDown,
  DropDownOption,
  DropDownStickyFooter,
  SectionDivider,
  Tooltip,
  Text,
  Preloader,
} from '@wix/wix-base-ui';
import * as quickNavigationMapper from './quickNavigationMapper';
import * as coreBi from '#packages/coreBi';
import * as symbols from '@wix/santa-editor-symbols';

const TooltipContent: React.FunctionComponent = () => (
  <div className="quick-navigation-pp__tooltip-content">
    <Text
      skin="secondary"
      enableEllipsis={false}
      size="medium"
      weight="bold"
      className="bubble-title-quick-navigation-pp"
    >
      {'TOPBAR_NEWPAGES_TOOLTIP_TITLE'}
    </Text>

    <Text skin="secondary" enableEllipsis={false} size="small">
      {'TOPBAR_NEWPAGES_TOOLTIP_BODY'}
    </Text>
  </div>
);

const {
  connect,
  STORES: { EDITOR_API },
} = util.hoc;

const isSelectedItemChanged = (item: Item, changedItem: Item) => {
  const isItemNew = !item && changedItem;
  const isItemIdChanged = item && item.id !== changedItem.id;
  const isItemRenamed =
    item && item.id === changedItem.id && item.title !== changedItem.title;
  return isItemNew || isItemIdChanged || isItemRenamed;
};

const getClassName = (item: Item) => {
  if (item.isNested) {
    return 'nested-page';
  }
  if (item.isFolder) {
    return 'folder';
  }
  if (item.isLink) {
    return 'link';
  }
};

const createSectionDivider = (label: string, key: string) =>
  React.createElement(
    SectionDivider,
    { className: 'upper', key },
    label.toUpperCase(),
  );

const createDropDownOption = (item: Item, key: string) =>
  React.createElement(DropDownOption, {
    value: item.id,
    label: item.title,
    shouldTranslate: false,
    disabled: item.isFolder || item.isLink,
    //@ts-expect-error
    optionWrapperClassName: getClassName(item),
    key,
  });

interface Item {
  title: string;
  isFolder?: boolean;
  isLink?: boolean;
  isNested?: boolean;
  id?: string;
}

interface Props {
  backToAdiTooltipVisibility?: boolean;
  getItems: () => Array<Item>;
  shouldShowLoader: boolean;
  closeBackToAdiTooltip: () => void;
  selectedItem: Item | null;
  onClickItem: (item: Item) => void;
  biEvent: (event: AnyFixMe, params: Record<string, any>) => void;
  shouldShrink: boolean;
  onClickManagePages: () => void;
  isManagePagesButtonVisible: boolean;
  updateRecentlyViewedPages: (item: Item) => void;
  forceOpenEventCounter: number;
  closeAllPanels: () => void;
  isSitePublished: boolean;
  topBarStateBIParamValue: string;
  isPopupOpened: boolean;
  closePopupPage: () => void;
}

class QuickNavigation extends React.Component<Props> {
  state = {
    items: this.props.selectedItem ? this.props.getItems() : [],
    forceOpen: false,
    withTooltip: true,
  };

  handleMouseEnter = () => {
    this.setState({ items: this.props.getItems() });
    if (this.props.backToAdiTooltipVisibility) {
      this.props.closeBackToAdiTooltip();
    }
  };
  getContent = () => {
    const content = [];

    let sectionIndex = 0;
    for (const item of this.state.items) {
      if (item.id) {
        // ids should be consistent, because they use this id to get current selected item, and dropdown is closed when selected item is changed
        content.push(createDropDownOption(item, `option-${item.id}`));
      } else {
        sectionIndex += 1;
        content.push(
          createSectionDivider(
            item.title || '',
            `divider-${item.title || sectionIndex}`,
          ),
        );
      }
    }
    return content;
  };
  getSelectedItemId = () => {
    if (!this.props.selectedItem) {
      return null;
    }

    return this.props.selectedItem.id;
  };
  handleClickItem = (id: string) =>
    this.props.onClickItem(this.state.items.find((item) => item.id === id));

  onClick = () => {
    util.editorWixRecorder.addLabel(
      `${quickNavigationMapper.PANEL_NAME} top bar clicked`,
    );
    this.props.biEvent(coreBi.events.topbar.top_bar_click, {
      category: quickNavigationMapper.PANEL_NAME,
      is_published: this.props.isSitePublished,
      state: this.props.topBarStateBIParamValue,
    });
    this.props.biEvent(coreBi.events.pages.quick_navigation_clicked, {
      origin: 'top_bar',
    });
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      isSelectedItemChanged(this.props.selectedItem, nextProps.selectedItem)
    ) {
      this.props.updateRecentlyViewedPages(nextProps.selectedItem);
      this.setState({ items: nextProps.getItems() });
    }
    const forceOpen =
      nextProps.forceOpenEventCounter > this.props.forceOpenEventCounter;
    if (forceOpen) {
      this.setState({ items: nextProps.getItems(), forceOpen: true });
    }
  }

  handleToggle = (isOpen: boolean) => {
    if (isOpen) {
      this.props.closeAllPanels();
    } else if (this.state.forceOpen) {
      this.setState({ forceOpen: false });
    }

    this.setState({ withTooltip: !isOpen });
  };

  render() {
    return (
      <Tooltip
        content={<TooltipContent />}
        marginBottom={5}
        maxWidth={220}
        closeOnMouseClick={true}
        className={util.cx('quick-navigation-pp', {
          minimized: this.props.shouldShrink,
        })}
        // it has to switch off the onMouseEnter event when the dropdown is opened because of FF issue
        openOnMouseEnter={this.state.withTooltip}
      >
        <div
          onMouseEnter={this.handleMouseEnter}
          onClick={this.onClick}
          className="quick-navigation-pp-content-wrapper"
        >
          {util.topBar.isNewTopBarEnabled() ? null : (
            <div
              onClick={this.props.closePopupPage}
              key="exit-lightbox"
              className={util.cx('exit-lightbox', {
                'active-exit-lightbox': this.props.isPopupOpened,
              })}
            >
              <span>{translate('PopUps_TopBar_Exit')}</span>
              <symbols.symbol name="zoomClose" />
            </div>
          )}
          <DropDown
            value={this.getSelectedItemId()}
            onChange={this.handleClickItem}
            shouldTranslate={false}
            searchBox={true}
            onToggle={this.handleToggle}
            forceOpen={this.state.forceOpen}
            dataLabel={translate('NewPages_QuickNav_Label')}
            optionsContainerClassName="dropdown-quick-navigation-pp"
          >
            {this.getContent()}
            {this.props.isManagePagesButtonVisible && (
              <DropDownStickyFooter closeOnClick={true}>
                <Button
                  onClick={this.props.onClickManagePages}
                  className="btn-text"
                >
                  <span className="manage-pages-text">
                    {translate('NewPages_QuickNav_Pages_Manage_Button')}
                  </span>
                </Button>
              </DropDownStickyFooter>
            )}
          </DropDown>
          {this.props.shouldShowLoader && (
            <div className="quick-navigation-pp-loader">
              <Preloader className="tiny dark" />
            </div>
          )}
        </div>
      </Tooltip>
    );
  }
}

const Connected = connect(
  EDITOR_API,
  quickNavigationMapper.mapStateToProps,
  quickNavigationMapper.mapDispatchToProps,
)(QuickNavigation);
Connected.pure = QuickNavigation;

export default Connected;
